import { injectable, inject } from 'inversify';
import { WindowOption } from 'openfin/_v2/api/window/windowOption';
import { Action } from '@openfin/predux';

import { Inject } from '../common/Injectables';
import { WebWindow, WebWindowFactory } from '../model/WebWindow';
import { ToggleCenterVisibility, ToggleCenterVisibilitySource, BlurCenter } from '../store/Actions';
import { ServiceStore } from '../store/ServiceStore';
import { MonitorModel } from '../model/MonitorModel';
import { TrayIcon } from '../model/TrayIcon';
import { RootState } from '../store/State';
import { renderRouterInWindow } from '../view/utils/RenderApp';
import { CenterRoutes } from '../view/routeMappings';
import { ROUTES } from '../view/routes';
import { centerHistory } from '../view/contexts/CenterHistory';
import { SetCenterPosition } from '../store/partials/Settings';
import { NOTIFICATION_CENTER_ANIMATION_DURATION, NOTIFICATION_CENTER_WIDTH } from '../constants';
import { SetSearchMode, ClearSearch } from '../store/partials/Search';

import { AsyncInit } from './AsyncInit';

const windowOptions: WindowOption = {
  name: 'Notification-Center',
  url: 'frame.html',
  autoShow: false,
  defaultHeight: 400,
  defaultWidth: 500,
  resizable: false,
  saveWindowState: false,
  defaultTop: 0,
  contextMenu: !(process.env.NODE_ENV === 'production'),
  frame: false,
  alwaysOnTop: true,
  icon: 'ui/favicon.ico',
  backgroundColor: '#2F3136',
  showTaskbarIcon: false,
  opacity: 0,
};

@injectable()
export class NotificationCenter extends AsyncInit {
  private readonly _monitorModel: MonitorModel;
  private readonly _store: ServiceStore;
  private readonly _trayIcon: TrayIcon;
  private readonly _webWindowFactory: WebWindowFactory;

  private _webWindow!: WebWindow;

  public constructor(
    @inject(Inject.MONITOR_MODEL) monitorModel: MonitorModel,
    @inject(Inject.STORE) store: ServiceStore,
    @inject(Inject.TRAY_ICON) trayIcon: TrayIcon,
    @inject(Inject.WEB_WINDOW_FACTORY) webWindowFactory: WebWindowFactory
  ) {
    super();

    this._monitorModel = monitorModel;
    this._store = store;
    this._store.onAction.add(this.onAction, this);
    this._trayIcon = trayIcon;
    this._webWindowFactory = webWindowFactory;
  }

  protected async init(): Promise<void> {
    await this._store.initialized;
    await this._monitorModel.initialized;

    // Ensure center window doesn't already exist
    try {
      const existingWindow = fin.Window.wrapSync({
        uuid: fin.Application.getCurrentSync().identity.uuid,
        name: windowOptions.name,
      });
      await existingWindow.close(true);

      // A window was closed, when there shouldn't really have been one
      console.warn('The notification center window already existed. Will be re-created.');
    } catch (e) {
      // This is good - the window shouldn't have existed
      // Nothing to do here.
    }

    // Create notification center app window
    try {
      this._webWindow = await this._webWindowFactory.createWebWindow(windowOptions);
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error('Notification Center window could not be created!', error.message);
      }
      throw error;
    }
    await this.hideWindowOffscreen();

    this._trayIcon.setIcon(
      `${window.location.href.split('/').slice(0, -1).join('/')}/ui/trayIcon.ico`
    );

    this._trayIcon.onLeftClick.add(() => {
      this._store.dispatch(new ToggleCenterVisibility(ToggleCenterVisibilitySource.TRAY));
    });

    await this.sizeToFit();
    await this.addListeners();

    if (this._store.state.centerVisible) {
      this.showWindow();
    }
    /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
    const domTarget = this._webWindow.document.getElementById('react-app')!;
    centerHistory.push(ROUTES.NOTIFICATIONS);
    renderRouterInWindow(CenterRoutes, this._webWindow, this._store, centerHistory, domTarget);
  }

  /**
   * The window visibility state.
   */
  public get visible(): boolean {
    const state = this._store.state;
    return state.centerVisible;
  }

  /**
   * Add listeners to the window.
   */
  private async addListeners(): Promise<void> {
    this._webWindow.onBlurred.add(() => {
      if (this.visible && !this._store.state.centerLocked) {
        this._store.dispatch(new BlurCenter());
      }
    });

    this._webWindow.document.addEventListener('keydown', (event) => {
      const showingSettings = centerHistory.location.pathname === ROUTES.SETTINGS;
      if (event.key === 'Escape') {
        if (this._store.state.search.searchMode) {
          this._store.dispatch(new SetSearchMode(false));
          this._store.dispatch(new ClearSearch());
        } else if (showingSettings) {
          centerHistory.push(ROUTES.NOTIFICATIONS);
        } else if (this._store.state.centerVisible) {
          this._store.dispatch(new ToggleCenterVisibility(ToggleCenterVisibilitySource.BUTTON));
        }
      } else if (
        event.ctrlKey &&
        event.key === 'f' &&
        !this._store.state.search.searchMode &&
        !showingSettings
      ) {
        this._store.dispatch(new SetSearchMode(true));
      }
    });

    this._monitorModel.onMonitorInfoChanged.add(async () => {
      await this.sizeToFit();
      await this.toggleWindow(this._store.state.centerVisible);
    });
  }

  private async onAction(action: Action<RootState>): Promise<void> {
    if (action instanceof ToggleCenterVisibility || action instanceof BlurCenter) {
      // Always go back to displaying the notification panel when hidden
      if (action instanceof ToggleCenterVisibility && !this.visible) {
        centerHistory.push(ROUTES.NOTIFICATIONS);
      }
      await this.toggleWindow(this._store.state.centerVisible);
    }
    if (action instanceof SetCenterPosition) {
      await this.changePosition();
    }
  }

  private async changePosition(): Promise<void> {
    await this.hideWindow();
    await this.sizeToFit();
    // We have to dispatch this action to keep the state up to date, when the window is hidden
    // a blur event is fired which sets the state of the center to not visible.
    await this._store.dispatch(
      new ToggleCenterVisibility(ToggleCenterVisibilitySource.INTERNAL, true)
    );
  }

  /**
   * Show the window.
   */
  public async showWindow(): Promise<void> {
    await this._webWindow.show();
    await this.animateIn();
    await this._webWindow.setAsForeground();
  }

  /**
   * Hide the window.
   * @param force Force the window to hide without animating out.
   */
  public async hideWindow(force?: boolean): Promise<void> {
    const duration = force ? 0 : NOTIFICATION_CENTER_ANIMATION_DURATION;
    await this.animateOut(duration);
    await this._webWindow.hide();
  }

  /**
   * Sets the window dimensions in shape of a side bar
   */
  private async sizeToFit(): Promise<void> {
    const { centerPosition } = this._store.state.settings;
    await this.hideWindow(true);
    const { monitorInfo } = this._monitorModel;
    const { availableRect } = monitorInfo.primaryMonitor;
    const left =
      centerPosition === 'right'
        ? availableRect.right - NOTIFICATION_CENTER_WIDTH
        : availableRect.left;
    const top = availableRect.top;

    // TODO [SERVICE-739] This is a fix for setBounds not working on hidden windows.
    await this._webWindow.showAt(left, top);

    await this._webWindow.setBounds({
      left,
      top,
      width: NOTIFICATION_CENTER_WIDTH,
      height: availableRect.bottom - availableRect.top,
    });

    // TODO [SERVICE-739] This is a fix for setBounds not working on hidden windows.
    await this._webWindow.hide();
  }

  private async hideWindowOffscreen() {
    const { virtualScreen, primaryMonitor } = this._monitorModel.monitorInfo;
    const height = primaryMonitor.availableRect.bottom;
    await this._webWindow.showAt(
      virtualScreen.left - NOTIFICATION_CENTER_WIDTH * 2,
      virtualScreen.top - height * 2
    );
    await this._webWindow.hide();
  }

  /**
   * Toggle window visibility.
   * @param visible Force the window to be shown or hidden. True = show, false = hide.
   */
  private async toggleWindow(visible: boolean): Promise<void> {
    if (visible) {
      return this.showWindow();
    } else {
      return this.hideWindow();
    }
  }

  /**
   * Animate the Notification Center window into view.
   * @param duration Animation duration.
   */
  private async animateIn(
    duration: number = NOTIFICATION_CENTER_ANIMATION_DURATION
  ): Promise<void> {
    return this._webWindow.animate(
      {
        opacity: {
          opacity: 1,
          duration,
        },
      },
      {
        interrupt: true,
        tween: 'ease-in-out',
      }
    );
  }

  /**
   * Animate the Notification Center window out of view.
   * @param duration Animation duration.
   */
  private async animateOut(duration = 400): Promise<void> {
    return this._webWindow.animate(
      {
        opacity: {
          opacity: 0,
          duration,
        },
      },
      {
        interrupt: true,
        tween: 'ease-in-out',
      }
    );
  }
}
